import { Button, Col, Form, Input, Row, Spin } from 'antd';
import '../../styles/_orgs.scss';
import { useCreateTeamByLocationIdMutation } from '../../features/organization/organizationService';
import useOrderToast from '../../hooks/useOrderToast';

type FieldType = {
    teamName?: string;
    rxLogicPracticeId?: string;
    scriptsurePracticeId?: string;
    teamCode?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: number;
    zipCode?: string;
    country: string;
    phoneNumber?: string;
    emailAddress?: string;
};

type Props = {
    selectedLocationId: string;
    setTeamModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const ExtraFieldLabel = ({ title }: { title: string }) => {
    return <span className="ms-3 text-semi-black-xs">{title}</span>;
};
const ValidationMessage = ({ message }: { message: string }) => {
    return <span className="ms-3">{message}</span>;
};

const OrganizationsNewTeamForm: React.FC<Props> = ({
    selectedLocationId,
    setTeamModalOpen,
}) => {
    const [form]: any = Form.useForm();
    const [createTeamByLocationId, { isLoading }] =
        useCreateTeamByLocationIdMutation();
    const { toast } = useOrderToast();
    const initialCountry = 'United States of America';

    const handleSubmit = async () => {
        const values = await form.validateFields();
        try {
            const response: any = await createTeamByLocationId({
                payload: {
                    ...values,
                    locationId: selectedLocationId,
                    country: 'United States of America',
                },
                location_id: selectedLocationId,
            });
            if (response && response.data && response.data.teamId) {
                toast('The team is successfully added to the location.', 'success', 2);
                setTeamModalOpen(false);
                form.resetFields();
            }
            else{
                toast('The team creation is failed. Please try again.', 'error', 2);
            }
        } catch (error) {
            toast('The team creation is failed. Please try again.', 'error', 2);
        }
    };
    return (
        <Spin spinning={isLoading}>
        <Form
            form={form}
            name="basic"
            layout="horizontal"
            autoComplete="off"
            className="orgs-new-team-form"
            initialValues={{ country: initialCountry }}
        >
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Team Name'}
                        name="teamName"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 11 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Team Name" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel
                                title="Practice name should align with Scripture and
                                RxLogic"
                            />
                        }
                    >
                        <Input
                            placeholder="Enter Practice Name"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="RxLogic’s Practice ID"
                        name="rxLogicPracticeId"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter RxLogic’s Practice ID" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="RxLogic’s Practice ID should align with RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter RxLogic’s Practice ID"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="ScriptSure’s Practice ID"
                        name="scriptsurePracticeId"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter ScriptSure’s Practice ID" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel
                                title="ScriptSure’s Practice ID should align with
                                ScriptSure"
                            />
                        }
                    >
                        <Input
                            placeholder="Enter ScrtiptSure’s Practice ID"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Team Code'}
                        name="teamCode"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 7 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Team Code" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Practice code should align with RxLogic" />
                        }
                    >
                        <Input
                            className="ms-3 bg-light-blue-thin rounded-1"
                            placeholder="Enter Practice Code"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Address Line 1'}
                        name="addressLine1"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 20 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Address Line 1" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Street Address, P.O. Box, Company Name, C/O" />
                        }
                    >
                        <Input
                            className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                            placeholder="Enter Address Line 1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Address Line 2'}
                        name="addressLine2"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 20 }}
                        extra={
                            <ExtraFieldLabel title="Apartment, Suite, Unit, Building, Floor, Etc." />
                        }
                    >
                        <Input
                            className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                            placeholder="Enter Address Line 2"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="City"
                        name="city"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter City" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter City"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="State/Province/Region"
                        name="state"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter State/Province/Region" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter State/Province/Region"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="ZIP/Postal Code"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        name="zipCode"
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter ZIP/Postal Code" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter ZIP/Postal Code"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="Country"
                        name="country"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input
                            className="ms-3 bg-light-blue-thin rounded-1 input-country"
                            disabled
                            onChange={() => {
                                form.setFieldsValue({
                                    country: initialCountry,
                                }); // Reset to initial value
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Phone Number'}
                        name="phoneNumber"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 7 }}
                    >
                        <Input
                            className="ms-3 bg-light-blue-thin rounded-1"
                            maxLength={10}
                            placeholder="(_ _ _) (_ _ _) (_ _ _ _)"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Email'}
                        name="emailAddress"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 7 }}
                    >
                        <Input
                            placeholder="Enter Email Address"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={4} offset={4}>
                    <Button
                        loading={isLoading}
                        disabled={isLoading}
                        type="primary"
                        className="w-75 ms-3 bg-light-blue border-0"
                        shape="round"
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
        </Spin>
    );
};

export default OrganizationsNewTeamForm;
